import React, { FunctionComponent, useMemo } from 'react';

import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { isAuthorized } from 'auth/Auth';
import { Filter, FilterName } from 'reviews2/feed/reviews-filter/ReviewsFilter.types';

interface Props {
    selectedReviewsFilter: Filter;
    onReviewsFilterSelect: (selectedFilter: Filter) => void;
    isNorthStarEnabled?: boolean;
}

export const DEFAULT_SELECTED_FILTER = {
    label: 'FILTER_ALL',
    value: FilterName.All,
};

export const FILTER_PENDING: Filter = {
    label: 'FILTER_PENDING',
    value: FilterName.Pending,
    divider: true,
};

export const FILTER_NEEDS_RESPONSE: Filter = {
    label: 'FILTER_NEEDS_RESPONSE',
    value: FilterName.NeedsResponse,
};

export const FILTER_NO_RESPONSE_REQUIRED: Filter = {
    label: 'FILTER_NO_RESPONSE_REQUIRED',
    value: FilterName.NoResponseRequired,
};

const FILTER_OPTIONS: Filter[] = [
    DEFAULT_SELECTED_FILTER,
    {
        label: 'FILTER_FLAGGED',
        value: FilterName.Flagged,
        divider: true,
    },
    {
        label: 'FILTER_UPDATED',
        value: FilterName.Updated,
    },
    {
        label: 'FILTER_UNREAD',
        value: FilterName.Unread,
    },
    {
        label: 'FILTER_HAS_COMMENT',
        value: FilterName.HasComment,
        divider: true,
    },
    {
        label: 'FILTER_HAS_MEDIA',
        value: FilterName.HasMedia,
    },
];

export const ReviewsFilter: FunctionComponent<Props> = ({
    selectedReviewsFilter,
    onReviewsFilterSelect,
    isNorthStarEnabled,
}) => {
    const { t } = useTranslation('reviews2');
    const isYetiEnabled = isAuthorized({ featureFlags: [FeatureFlagId.ReviewYetiDataFetcher] });
    const isReviews3NoResponseRequiredEnabled = isAuthorized({
        featureFlags: [FeatureFlagId.Reviews3NoResponseRequired],
    });
    const reviewFilters = useMemo(() => {
        const options = [...FILTER_OPTIONS];

        if (isYetiEnabled) {
            options.push(FILTER_PENDING, FILTER_NEEDS_RESPONSE);
        }

        if (isReviews3NoResponseRequiredEnabled) {
            options.push(FILTER_NO_RESPONSE_REQUIRED);
        }

        return options;
    }, [isYetiEnabled, isReviews3NoResponseRequiredEnabled]);

    return (
        <div className="r4e-reviews-filter">
            <Dropdown className={`${isNorthStarEnabled ? 'north-star' : ''}`}>
                <Dropdown.Toggle variant="secondary" id="reviews-filter">
                    {t(selectedReviewsFilter.label)}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    {reviewFilters.map((filter) => {
                        return (
                            <div key={filter.value}>
                                {filter.divider && <Dropdown.Divider />}
                                <Dropdown.Item
                                    onClick={(e): void => {
                                        onReviewsFilterSelect(filter);
                                    }}
                                >
                                    {t(filter.label)}
                                    {selectedReviewsFilter.value === filter.value && <i className="icon-check" />}
                                </Dropdown.Item>
                            </div>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};
